import { createWorker } from 'tesseract.js';
import { ITEM_CATEGORIES, METAL_TYPES } from '../constants';

// ── PDF → Canvas → Image ───────────────────────────────────────────────────
async function pdfToImageDataUrl(file) {
  const pdfjsLib = await import('pdfjs-dist/build/pdf');
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

  const arrayBuffer = await file.arrayBuffer();
  const pdf         = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const page        = await pdf.getPage(1); // first page
  const viewport    = page.getViewport({ scale: 2.5 }); // high-res for better OCR

  const canvas    = document.createElement('canvas');
  canvas.width    = viewport.width;
  canvas.height   = viewport.height;
  const ctx       = canvas.getContext('2d');
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas.toDataURL('image/png');
}

// ── RUN OCR ────────────────────────────────────────────────────────────────
export async function runOCR(file, onProgress) {
  let imageSource;

  if (file.type === 'application/pdf') {
    onProgress && onProgress(5, 'Rendering PDF…');
    imageSource = await pdfToImageDataUrl(file);
  } else {
    imageSource = file; // Tesseract accepts File objects directly for images
  }

  onProgress && onProgress(15, 'Starting OCR engine…');
  const worker = await createWorker('eng', 1, {
    logger: (m) => {
      if (m.status === 'recognizing text') {
        onProgress && onProgress(15 + Math.round(m.progress * 70), `Scanning text… ${Math.round(m.progress * 100)}%`);
      }
    },
  });

  onProgress && onProgress(85, 'Parsing results…');
  const { data: { text } } = await worker.recognize(imageSource);
  await worker.terminate();

  onProgress && onProgress(95, 'Extracting invoice data…');
  return text;
}

// ── INVOICE PARSER ─────────────────────────────────────────────────────────
// Heuristic parser — works well on clean dealer/auction invoices
export function parseInvoiceText(rawText) {
  const lines  = rawText.split('\n').map(l => l.trim()).filter(Boolean);
  const text   = rawText.toUpperCase();
  const result = {
    invoiceNumber: '',
    vendor:        '',
    date:          '',
    lineItems:     [],
    rawText,
  };

  // ── Invoice number ──
  for (const line of lines) {
    const m = line.match(/(?:invoice\s*#?|inv\.?\s*#?|order\s*#?)[:\s]*([A-Z0-9\-]{3,20})/i);
    if (m) { result.invoiceNumber = m[1].trim(); break; }
  }
  // Fallback: look for standalone number patterns like INV-0012 or #12345
  if (!result.invoiceNumber) {
    for (const line of lines) {
      const m = line.match(/^#?([A-Z]{0,4}\d{3,10})$/i);
      if (m) { result.invoiceNumber = m[1]; break; }
    }
  }

  // ── Vendor / Company name (usually first 3 lines, longest one) ──
  const topLines = lines.slice(0, 6).filter(l => l.length > 3 && l.length < 60 && !/invoice|date|order|#/i.test(l));
  if (topLines.length > 0) {
    result.vendor = topLines.sort((a, b) => b.length - a.length)[0];
  }

  // ── Date ──
  for (const line of lines) {
    const m = line.match(/(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})|(\d{4}[\/\-]\d{2}[\/\-]\d{2})/);
    if (m) {
      const raw = m[0];
      try {
        const parsed = new Date(raw);
        if (!isNaN(parsed)) {
          result.date = parsed.toISOString().split('T')[0];
          break;
        }
      } catch {}
    }
  }
  // Fallback: written-out dates like "March 15, 2024" or "15 March 2024"
  if (!result.date) {
    for (const line of lines) {
      const m = line.match(/(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\w*\s+(\d{4})/i)
             || line.match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\w*\s+(\d{1,2}),?\s+(\d{4})/i);
      if (m) {
        const parsed = new Date(line.match(/\d{1,2}.*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec).*/i)?.[0] || line);
        if (!isNaN(parsed)) { result.date = parsed.toISOString().split('T')[0]; break; }
      }
    }
  }

  // ── Line items ──
  // Strategy: find lines with a price pattern ($XXX or XXX.XX) and try to parse qty + description
  const pricePattern   = /\$?\s*(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s*$/;
  const qtyPattern     = /^(\d+(?:\.\d+)?)\s*[xX@]?\s+/;
  const weightPattern  = /(\d+(?:\.\d+)?)\s*(?:oz|troy|t\.oz|ozt|gram|g\b)/i;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Skip header/footer-like lines
    if (/total|subtotal|tax|shipping|discount|thank|payment|balance|due|remit|page|www\.|@/i.test(line)) continue;
    if (line.length < 5) continue;

    const priceMatch = line.match(pricePattern);
    if (!priceMatch) continue;

    const unitPrice = parseFloat(priceMatch[1].replace(/,/g, ''));
    if (unitPrice <= 0 || unitPrice > 10_000_000) continue;

    let description = line.replace(pricePattern, '').trim();
    let qty = 1;

    // Extract qty from start of description
    const qtyMatch = description.match(qtyPattern);
    if (qtyMatch) {
      qty = parseFloat(qtyMatch[1]);
      description = description.replace(qtyPattern, '').trim();
    }

    // Extract weight
    const wtMatch = line.match(weightPattern);
    const weight  = wtMatch ? parseFloat(wtMatch[1]) : 0;

    // Detect metal type
    let metal = detectMetal(line);

    // Detect category
    let category = detectCategory(line);

    // Only add if description is meaningful
    if (description.length < 3) continue;

    result.lineItems.push({
      description: cleanDescription(description),
      qty:         qty || 1,
      unitPrice,
      totalAmount: (qty || 1) * unitPrice,
      metal,
      category,
      weight,
    });
  }

  // If no line items found with strict price-at-end, try looser pass
  if (result.lineItems.length === 0) {
    for (const line of lines) {
      if (/total|subtotal|tax|shipping/i.test(line)) continue;
      const prices = [...line.matchAll(/\$?\s*(\d{1,3}(?:,\d{3})*\.\d{2})/g)];
      if (prices.length === 0) continue;
      const unitPrice = parseFloat(prices[prices.length - 1][1].replace(/,/g, ''));
      if (unitPrice <= 0) continue;
      const description = line.replace(/\$?\s*\d[\d,\.]+/g, '').trim();
      if (description.length < 3) continue;
      result.lineItems.push({
        description:  cleanDescription(description),
        qty:          1,
        unitPrice,
        totalAmount:  unitPrice,
        metal:        detectMetal(line),
        category:     detectCategory(line),
        weight:       0,
      });
    }
  }

  return result;
}

// ── HELPERS ────────────────────────────────────────────────────────────────
function detectMetal(text) {
  const t = text.toUpperCase();
  if (/GOLD|AU\b|AGE|AEA|EAGLE|MAPLE|KRUGER|BUFFALO|PANDA|SOVEREIGN|LIBERTAD/.test(t)) return 'Gold';
  if (/SILVER|AG\b|ASE|MORGAN|PEACE|WALKER|FRANKLIN|KENNEDY|MERC|MERCURY|DIME|QUARTER|HALF/.test(t)) return 'Silver';
  if (/PLATINUM|PT\b|PLAT/.test(t)) return 'Platinum';
  if (/PALLADIUM|PD\b/.test(t)) return 'Palladium';
  return 'Gold'; // default
}

function detectCategory(text) {
  const t = text.toUpperCase();
  const metal = detectMetal(text);

  if (/BAR|KILO|OZ BAR|GRAM BAR/.test(t))                    return `${metal} Bar`;
  if (/COIN|EAGLE|MAPLE|KRUGER|BUFFALO|SOVEREIGN|PANDA|LIBERTAD|PHILHARMONIC/.test(t)) return `${metal} Coin`;
  if (/RING|BRACELET|NECKLACE|CHAIN|EARRING|PENDANT|JEWELRY|BANGLE/.test(t)) return `${metal} Jewelry`;
  if (/MORGAN|PEACE|WALKER|FRANKLIN|KENNEDY|MERC|BARBER|SEATED|TRADE/.test(t)) return 'Silver Coin';
  if (/NUMISMATIC|PROOF|COMMEM|TYPE\s*COIN/.test(t))          return 'Numismatic/Collectible';
  if (/SCRAP|MELT|JUNK|90%|40%/.test(t))                     return 'Scrap Metal';
  if (/BILL|NOTE|CURRENCY|PAPER/.test(t))                     return 'Currency / Paper';
  return `${metal} Coin`; // default
}

function cleanDescription(desc) {
  return desc
    .replace(/^\W+/, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/[|\\\/]{2,}/g, '')
    .trim();
}
